import React, { Suspense, lazy } from 'react';
import { Grid, CircularProgress } from '@mui/material';
import PimcoreBrick from '@src/models/Pimcore/PimcoreBrick.interface';
import VehicleType from '@src/models/VehicleType.model';

const linksBrickResolve = lazy(() => import('./brickresolvers/links'));
const wysiwygBrickResolve = lazy(() => import('./brickresolvers/wysiwyg'));
const stepBrickResolve = lazy(() => import('./brickresolvers/step'));
const accordionBrickResolve = lazy(() => import('./brickresolvers/accordion'));
const informationBrickResolve = lazy(() => import('./brickresolvers/information'));
const imageAndTextBrickResolve = lazy(() => import('./brickresolvers/imageAndText'));
const newImageAndTextBrickResolve = lazy(() => import('./brickresolvers/newImageAndText'));
const stationFinderBrickResolve = lazy(() => import('./brickresolvers/stationFinder'));
const snippetBrickResolve = lazy(() => import('./brickresolvers/snippet'));
const appBrickResolve = lazy(() => import('./brickresolvers/app'));
const newAppBrickResolve = lazy(() => import('./brickresolvers/newApp'));
const carsBrickResolve = lazy(() => import('./brickresolvers/cars'));
const newCarsBrickResolve = lazy(() => import('./brickresolvers/newCars'));
const membershipBrickResolve = lazy(() => import('./brickresolvers/membership'));
const carBrickResolve = lazy(() => import('./brickresolvers/car'));
const newCarBrickResolve = lazy(() => import('./brickresolvers/newCar'));
const cookiesBrickResolve = lazy(() => import('./brickresolvers/cookies'));
const howToBrickResolve = lazy(() => import('./brickresolvers/howTo'));
const longHowToBrickResolve = lazy(() => import('./brickresolvers/longHowTo'));
const quoteBrickResolve = lazy(() => import('./brickresolvers/quote'));
const uspBrickResolve = lazy(() => import('./brickresolvers/usp'));
const heroBrickResolve = lazy(() => import('./brickresolvers/hero'));
const videoBrickResolve = lazy(() => import('./brickresolvers/video'));
const leadBrickResolve = lazy(() => import('./brickresolvers/lead'));
const leadHeroImageBrickResolve = lazy(() => import('./brickresolvers/leadHeroImage'));
const membershipIntroBrickResolve = lazy(() => import('./brickresolvers/membershipIntro'));
const mapBrickResolve = lazy(() => import('./brickresolvers/map'));
const columnCheckBrickResolve = lazy(() => import('./brickresolvers/columnCheck'));
const marketingConsentsBrickResolve = lazy(() => import('./brickresolvers/marketingConsents'));
const flexBookingBrickResolve = lazy(() => import('./brickresolvers/flexBooking'));
const flexBookCarResolve = lazy(() => import('./brickresolvers/flexBookCar'));

export const findEditableByName = (editables, name) => {
	return editables.find(editable => editable.name === name);
};
type BrickResolverParams = {
	location: any;
	pageContext: any;
	vehicleTypes: VehicleType[];
};

export const BrickResolver = ({ location, pageContext, vehicleTypes }: BrickResolverParams) => {
	let bricksList = [];

	if (!pageContext || !pageContext.data || (pageContext.data && pageContext.data.blocks.length === 0)) {
		return (
			<div style={{ textAlign: 'center' }}>
				<CircularProgress />
			</div>
		);
	}

	const pushBrick = currentBrickList => {
		if (currentBrickList.length >= 3) {
			bricksList.push(<Grid key={`grid-${bricksList.length}`}>{currentBrickList}</Grid>);
		} else {
			bricksList.push(currentBrickList);
		}
	};

	pageContext.data.blocks.forEach((block, blockIndex) => {
		if (!block.bricks || block.bricks.length === 0) {
			return;
		}

		let lastBrickType = null;
		let currentBrickList = [];

		block.bricks.forEach((brick: PimcoreBrick, index) => {
			let currentBrick = null;

			let brickType = brick.type;
			brick.location = location;
			//Pass parent data to all components (Graphql Queries, usually Pimcore data objects)
			brick.data = {
				vehicleTypes: vehicleTypes,
			};

			brick.brickMeta = {
				renderIndex: index,
				key: blockIndex + '-' + index,
			};

			switch (brickType) {
				case 'step-brick':
					currentBrick = React.createElement(stepBrickResolve, brick);
					break;
				case 'link-brick':
					currentBrick = React.createElement(linksBrickResolve, brick);
					break;
				case 'wysiwyg-brick':
					currentBrick = React.createElement(wysiwygBrickResolve, brick);
					break;
				case 'accordion-brick':
					currentBrick = React.createElement(accordionBrickResolve, brick);
					break;
				case 'information-brick':
					currentBrick = React.createElement(informationBrickResolve, brick);
					break;
				case 'station-finder-brick':
					currentBrick = React.createElement(stationFinderBrickResolve, brick);
					break;
				case 'image-and-text-brick':
					currentBrick = React.createElement(imageAndTextBrickResolve, brick);
					break;
				case 'new-image-and-text-brick':
					currentBrick = React.createElement(newImageAndTextBrickResolve, brick);
					break;
				case 'app-brick':
					currentBrick = React.createElement(appBrickResolve, brick);
					break;
				case 'new-app-brick':
					currentBrick = React.createElement(newAppBrickResolve, brick);
					break;
				case 'cars-brick':
					currentBrick = React.createElement(carsBrickResolve, brick);
					break;
				case 'new-cars-brick':
					currentBrick = React.createElement(newCarsBrickResolve, brick);
					break;
				case 'snippet':
					currentBrick = React.createElement(snippetBrickResolve, brick);
					break;
				case 'membership-brick':
					currentBrick = React.createElement(membershipBrickResolve, brick);
					break;
				case 'marketing-consents-brick':
					currentBrick = React.createElement(marketingConsentsBrickResolve, brick);
					break;
				case 'car-brick':
					currentBrick = React.createElement(carBrickResolve, brick);
					break;
				case 'new-car-brick':
					currentBrick = React.createElement(newCarBrickResolve, brick);
					break;
				case 'cookies-brick':
					currentBrick = React.createElement(cookiesBrickResolve, brick);
					break;
				case 'how-to-brick':
					currentBrick = React.createElement(howToBrickResolve, brick);
					break;
				case 'long-how-to-brick':
					currentBrick = React.createElement(longHowToBrickResolve, brick);
					break;
				case 'quote-brick':
					currentBrick = React.createElement(quoteBrickResolve, brick);
					break;
				case 'usp-brick':
					currentBrick = React.createElement(uspBrickResolve, brick);
					break;
				case 'hero-brick':
					currentBrick = React.createElement(heroBrickResolve, brick);
					break;
				case 'video-brick':
					currentBrick = React.createElement(videoBrickResolve, brick);
					break;
				case 'lead-brick':
					currentBrick = React.createElement(leadBrickResolve, brick);
					break;
				case 'lead-hero-image-brick':
					currentBrick = React.createElement(leadHeroImageBrickResolve, brick);
					break;
				case 'membership-intro-brick':
					currentBrick = React.createElement(membershipIntroBrickResolve, brick);
					break;
				case 'map-brick':
					currentBrick = React.createElement(mapBrickResolve, brick);
					break;
				case 'column-check-brick':
					currentBrick = React.createElement(columnCheckBrickResolve, brick);
					break;
				case 'flex-booking-brick':
					currentBrick = React.createElement(flexBookingBrickResolve, brick);
					break;
				case 'flex-book-car-brick':
					currentBrick = React.createElement(flexBookCarResolve, brick);
					break;
				default:
				// throw exception or just ignore case when brick is not found
			}

			if (!currentBrick) {
				return;
			}

			if (lastBrickType !== null && lastBrickType !== brickType) {
				pushBrick(currentBrickList);

				currentBrickList = [];
			}
			currentBrickList.push(currentBrick);
			lastBrickType = brickType;
		});

		pushBrick(currentBrickList);
	});

	return (
		<>
			<Suspense
				fallback={
					<div className="loading-container">
						<CircularProgress size="15rem" />
					</div>
				}
			>
				{bricksList}
			</Suspense>
		</>
	);
};
